<template>
  <div class="pb-page fv">
    <PageHeader title="Collection forms" :subtitle="'Branded feedback pages for ' + withStop(brand ? brand.name : 'your brand') + ' Share a link or a QR code; customers need no account.'">
      <LockedAction v-if="newCap" feature="forms" look="pb-button" icon="mdi-plus" label="New collection page" :cap="newCap" :used="activeForms" />
      <button v-else type="button" class="pb-button" @click="openCreate"><i class="mdi mdi-plus pb-ico" aria-hidden="true"></i>New collection page</button>
    </PageHeader>

    <SkeletonList v-if="loading" :rows="3" label="Loading collection pages…" />
    <div v-else-if="error" class="pb-alert is-error" role="alert"><i class="mdi mdi-alert-circle-outline pb-ico" aria-hidden="true"></i><span>{{ error }}</span>
      <button type="button" class="pb-button sm outline" @click="load">Retry</button></div>
    <EmptyState v-else-if="!forms.length" icon="mdi-form-select" title="Create your first collection page"
                text="Start from a template. You can change the questions and the look at any time.">
      <button type="button" class="pb-button" @click="openCreate">Create</button>
    </EmptyState>

    <div v-else class="fv-list">
      <article v-for="form in visibleForms" :key="form.id" :class="['pb-card', 'fv-item', form.status]">
        <div class="fv-main">
          <div class="pb-row fv-top">
            <h2 class="fv-name"><router-link :to="'/forms/' + form.id">{{ form.name }}</router-link></h2>
            <span :class="['pb-chip', chipClass(form)]">{{ statusLabel(form) }}</span>
          </div>
          <p class="pb-muted">{{ form.content.questions.length }} question{{ form.content.questions.length === 1 ? '' : 's' }} · {{ form.submissions }} response{{ form.submissions === 1 ? '' : 's' }}</p>
          <p v-if="form.refused" class="pb-help fv-refused">{{ form.refused }}</p>
          <p class="fv-link pb-mono">{{ form.url }}</p>
        </div>
        <div class="pb-actions fv-actions">
          <button type="button" class="pb-button sm outline" @click="copy(form)"><i class="mdi mdi-content-copy pb-ico" aria-hidden="true"></i>Copy link</button>
          <router-link :to="'/forms/' + form.id" class="pb-button sm outline">Edit &amp; share</router-link>
          <details class="fv-more">
            <summary class="pb-button sm ghost" aria-label="More actions"><i class="mdi mdi-dots-horizontal pb-ico" aria-hidden="true"></i></summary>
            <div class="fv-menu">
              <button v-if="form.status !== 'active'" type="button" class="fv-menu-item" @click="setStatus(form, 'active')">Turn on</button>
              <button v-if="form.status === 'active'" type="button" class="fv-menu-item" @click="setStatus(form, 'paused')">Pause</button>
              <button v-if="form.status !== 'archived'" type="button" class="fv-menu-item" @click="setStatus(form, 'archived')">Archive</button>
              <LockedAction v-if="newCap" feature="forms" look="fv-menu-item" label="Duplicate" :used="activeForms" @locked="closeMenus" />
              <button v-else type="button" class="fv-menu-item" @click="duplicate(form, brandId)">Duplicate</button>
              <template v-if="otherBrands.length">
                <button v-for="other in otherBrands" :key="other.id" type="button" class="fv-menu-item" @click="duplicate(form, other.id)">Copy to {{ other.name }}</button>
              </template>
              <template v-if="can('templates')">
                <button v-if="limits.agency_templates" type="button" class="fv-menu-item" @click="saveTemplate(form)">Save as template</button>
                <LockedAction v-else feature="agency_templates" look="fv-menu-item" label="Save as template" @locked="closeMenus" />
              </template>
            </div>
          </details>
        </div>
      </article>
      <button v-if="archivedCount && !showArchived" type="button" class="pb-button ghost" @click="showArchived = true">Show {{ archivedCount }} archived</button>
    </div>

    <v-dialog v-model="createOpen" max-width="720" scrollable>
      <div class="pb-dialog fv-dialog">
        <div class="pb-dialog-head"><h2>Start from a template</h2>
          <button type="button" class="pb-icon-button" aria-label="Close" @click="createOpen = false"><i class="mdi mdi-close" aria-hidden="true"></i></button></div>
        <p>Each template asks 3 or 4 short questions. One of them becomes the quote you publish.</p>
        <div class="fv-templates" role="radiogroup" aria-label="Templates">
          <button v-for="item in templates" :key="item.key" type="button" role="radio" :aria-checked="pick === item.key ? 'true' : 'false'"
                  :aria-disabled="item.locked ? 'true' : null" :class="['fv-template', { picked: pick === item.key, locked: item.locked }]" @click="choose(item)">
            <span class="fv-t-name">{{ item.name }}<PlanChip v-if="item.locked" :plan="availableOn('template_library')" /></span>
            <span class="pb-muted">{{ item.description }}</span>
            <span class="fv-t-q">{{ item.questions.map(q => q.label).slice(0, 2).join(' · ') }}…</span>
          </button>
        </div>
        <div v-if="savedTemplates.length" class="fv-saved">
          <p class="pb-label">Your saved templates</p>
          <div class="pb-actions"><button v-for="item in savedTemplates" :key="item.id" type="button" :class="['pb-button', 'sm', pickSaved === item.id ? '' : 'outline']" @click="pickSaved = item.id; pick = ''">{{ item.name }}</button></div>
        </div>
        <div v-if="createError" class="pb-alert is-error" role="alert"><span>{{ createError }}</span></div>
        <div class="pb-dialog-actions">
          <button type="button" class="pb-button ghost" @click="createOpen = false">Cancel</button>
          <button type="button" class="pb-button" :disabled="(!pick && !pickSaved) || busy" @click="create">{{ busy ? 'Creating…' : 'Create page' }}</button>
        </div>
      </div>
    </v-dialog>
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" :stacked="createOpen" />
  </div>
</template>

<script>
import EmptyState from '../components/EmptyState.vue';
import LockedAction from '../components/LockedAction.vue';
import PageHeader from '../components/PageHeader.vue';
import PlanChip from '../components/PlanChip.vue';
import SkeletonList from '../components/SkeletonList.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import { copyText } from '../components/SnippetBox.vue';
import workspace from '../mixins/workspace';
import { get, post, message, planLimit } from '../api';
import { toast } from '../lib/toast';
import { limitReached } from '../lib/upgrades';
import { collectUrl } from '../publicUrls';
import { withStop } from '../lib/format';

export default {
  name: 'FormsView',
  components: { EmptyState, LockedAction, PageHeader, PlanChip, SkeletonList, UpgradeDialog },
  mixins: [workspace],
  data: () => ({ forms: [], loading: true, error: '', showArchived: false, createOpen: false, templates: [], savedTemplates: [],
    pick: 'general', pickSaved: null, busy: false, createError: '', upgrade: null, upgradeOpen: false }),
  computed: {
    visibleForms() { return this.forms.filter(form => this.showArchived || form.status !== 'archived'); },
    archivedCount() { return this.forms.filter(form => form.status === 'archived').length; },
    otherBrands() { return this.brands.filter(item => item.id !== this.brandId); },
    // Plans with a page limit have one brand, so its active pages are the workspace's.
    activeForms() { return this.forms.filter(form => form.status === 'active').length; },
    newCap() { return this.loading ? '' : this.capOf('forms', this.activeForms); }
  },
  watch: { brandId() { this.load(); } },
  async mounted() {
    if (!(await this.requireWorkspace())) return;
    this.load();
  },
  methods: {
    withStop,
    // An active page the public pages refuse (a downgrade's 30 days are over, or a paused brand): say so.
    statusLabel(form) {
      if (form.refused) return 'Not accepting responses';
      return { active: 'Accepting responses', paused: 'Paused', archived: 'Archived' }[form.status] || form.status;
    },
    chipClass(form) {
      if (form.refused) return 'is-warning';
      return { active: 'approved', paused: 'is-warning', archived: 'archived' }[form.status] || '';
    },
    async load() {
      if (!this.brandId) return;
      this.loading = true;
      this.error = '';
      try { this.forms = (await get('/brands/' + this.brandId + '/forms')).forms || []; } catch (error) { this.error = message(error); } finally { this.loading = false; }
    },
    fail(error) {
      const limit = planLimit(error);
      if (limit) { this.createOpen = false; this.upgrade = limit; this.upgradeOpen = true; return true; }
      toast(message(error), 'error');
      return false;
    },
    async openCreate() {
      // At the plan's limit the upgrade note comes first, not a template picker that ends in it. Plans with a form
      // limit have one brand, so its active pages are the workspace's.
      const reached = limitReached('forms', this.limits, this.activeForms, this.availableOn('forms'));
      if (reached) { this.upgrade = reached; this.upgradeOpen = true; return; }
      this.createError = '';
      this.createOpen = true;
      try {
        if (!this.templates.length) this.templates = (await get('/question-templates')).templates || [];
        if (this.limits.agency_templates) this.savedTemplates = (await get('/templates', { kind: 'form' })).templates || [];
      } catch (error) { this.createError = message(error); }
    },
    // A locked template opens the upgrade note over the picker: "Not now" returns to it with the choice unchanged.
    choose(item) {
      if (item.locked) { this.upgrade = { feature: 'template_library' }; this.upgradeOpen = true; return; }
      this.pick = item.key;
      this.pickSaved = null;
    },
    async create() {
      this.busy = true;
      this.createError = '';
      try {
        const body = this.pickSaved ? { template_id: this.pickSaved } : { template_key: this.pick };
        const form = await post('/brands/' + this.brandId + '/forms', body);
        this.createOpen = false;
        this.refreshWorkspace();
        this.$router.push('/forms/' + form.id).catch(() => {});
      } catch (error) {
        if (!planLimit(error)) this.createError = message(error); else this.fail(error);
      } finally { this.busy = false; }
    },
    async setStatus(form, status) {
      try {
        const updated = await post('/forms/' + form.id + '/status', { status });
        Object.assign(form, updated);
        toast(status === 'active' ? 'The page accepts responses again.' : status === 'paused' ? 'Paused. The link shows that it isn\'t accepting responses.' : 'Archived.');
        this.refreshWorkspace();
      } catch (error) { this.fail(error); }
      this.closeMenus();
    },
    async duplicate(form, brandId) {
      try {
        const copy = await post('/forms/' + form.id + '/duplicate', { brand_id: brandId });
        toast('Copied. Responses are never copied.');
        this.refreshWorkspace();
        if (brandId === this.brandId) this.forms.push(copy);
      } catch (error) { this.fail(error); }
      this.closeMenus();
    },
    async saveTemplate(form) {
      try {
        await post('/templates', { kind: 'form', name: form.name, source_id: form.id });
        toast('Saved as a template for your other brands.');
      } catch (error) { this.fail(error); }
      this.closeMenus();
    },
    closeMenus() { this.$el.querySelectorAll('details[open]').forEach(node => node.removeAttribute('open')); },
    async copy(form) { await copyText(collectUrl(form)); toast('Link copied.'); }
  }
};
</script>

<style scoped>
.fv-list{display:grid;gap:12px;justify-items:start}
.fv-item{width:100%;display:flex;align-items:center;justify-content:space-between;gap:16px;padding:20px 22px}
.fv-item.archived{opacity:.75;box-shadow:none}
.fv-main{min-width:0}
.fv-top{flex-wrap:wrap;gap:10px;margin-bottom:4px}
.fv-name{font:600 17px/1.3 var(--font-body);margin:0}
.fv-name a{color:#1F2328;text-decoration:none}
.fv-name a:hover{text-decoration:underline;text-underline-offset:3px}
.fv-link{margin:6px 0 0;font-size:13px;color:#6B635B;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.fv-actions{flex:0 0 auto}
.fv-more{position:relative}
.fv-more summary{list-style:none}
.fv-more summary::-webkit-details-marker{display:none}
.fv-menu{position:absolute;right:0;top:calc(100% + 6px);z-index:5;min-width:220px;padding:6px;background:#fff;border:1px solid #E6DED2;border-radius:12px;box-shadow:var(--pb-shadow-raised)}
.fv-menu-item{display:flex;width:100%;min-height:44px;align-items:center;padding:8px 12px;border:0;border-radius:8px;background:none;font:500 14px var(--font-body);color:#1F2328;cursor:pointer;text-align:left}
.fv-menu-item:hover{background:#F3EEE6}
.fv-menu-item.is-locked{justify-content:space-between;gap:12px;white-space:nowrap}
.fv-templates{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(200px,100%),1fr));gap:10px;margin:16px 0}
.fv-template{display:flex;flex-direction:column;align-items:stretch;gap:6px;text-align:left;padding:14px;border:1.5px solid #E6DED2;border-radius:12px;background:#fff;cursor:pointer;min-height:44px}
.fv-template:hover{border-color:#D4C9B8}
.fv-template.picked{border-color:#B4472B;box-shadow:0 0 0 3px rgba(180,71,43,.15)}
.fv-template.locked{background:#FBF8F3}
.fv-t-name{display:flex;justify-content:space-between;gap:8px;font:700 14.5px/1.3 var(--font-body);color:#1F2328}
.fv-t-name .mdi{color:#6B635B}
.fv-t-q{font-size:13px;line-height:1.45;color:#4F4A45}
.fv-saved{margin-bottom:12px}
@media(max-width:700px){
  .fv-item{flex-direction:column;align-items:stretch}
  .fv-actions .pb-button{flex:1}
}
</style>
